import { useEffect, useRef, useState } from 'react';

const GOOGLE_SCRIPT_ID = 'google-identity-services';
const GOOGLE_SCRIPT_URL = 'https://accounts.google.com/gsi/client';

type UseGoogleScriptReturn = {
  /** Whether the script is currently loading */
  isLoading: boolean;
  /** Error that occurred during script loading */
  error: Error | null;
  /** Whether the script has been successfully loaded */
  isLoaded: boolean;
};

/**
 * Custom hook to dynamically load the Google Identity Services script
 * @returns Object containing loading state, error state, and loaded state
 */
export const useGoogleScript = (): UseGoogleScriptReturn => {
  const [isLoading, setIsLoading] = useState(() => !window.google?.accounts);
  const [error, setError] = useState<Error | null>(null);
  const [isLoaded, setIsLoaded] = useState(() => !!window.google?.accounts);
  const scriptLoadedRef = useRef(false);

  useEffect(() => {
    // Check if script is already loaded
    if (window.google?.accounts) {
      scriptLoadedRef.current = true;
      return;
    }

    // Check if script element already exists
    const existingScript = document.getElementById(GOOGLE_SCRIPT_ID);
    if (existingScript) {
      // Script exists but might not be loaded yet
      const handleLoad = () => {
        setIsLoading(false);
        setIsLoaded(true);
        scriptLoadedRef.current = true;
      };

      const handleError = () => {
        setError(new Error('Failed to load Google Identity Services script'));
        setIsLoading(false);
      };

      existingScript.addEventListener('load', handleLoad);
      existingScript.addEventListener('error', handleError);

      return () => {
        existingScript.removeEventListener('load', handleLoad);
        existingScript.removeEventListener('error', handleError);
      };
    }

    // Create and append script element
    const script = document.createElement('script');
    script.id = GOOGLE_SCRIPT_ID;
    script.src = GOOGLE_SCRIPT_URL;
    script.async = true;
    script.defer = true;

    const handleLoad = () => {
      if (window.google?.accounts) {
        setIsLoading(false);
        setIsLoaded(true);
        scriptLoadedRef.current = true;
      } else {
        setError(new Error('Google Identity Services not available after script load'));
        setIsLoading(false);
      }
    };

    const handleError = () => {
      setError(new Error('Failed to load Google Identity Services script'));
      setIsLoading(false);
    };

    script.addEventListener('load', handleLoad);
    script.addEventListener('error', handleError);

    document.head.appendChild(script);

    // Cleanup function
    return () => {
      script.removeEventListener('load', handleLoad);
      script.removeEventListener('error', handleError);

      // Only remove script if this component instance loaded it
      if (scriptLoadedRef.current && document.getElementById(GOOGLE_SCRIPT_ID)) {
        // Note: We don't actually remove the script to prevent issues with other components
        // The script can be safely reused across the application
      }
    };
  }, []);

  return { isLoading, error, isLoaded };
};
